import { once, sleep, UnknownFunction } from '@voithru/front-core';
import ManualPromise, { PromiseReject, PromiseResolve } from './ManualPromise';
import Queue, { ReadonlyQueue } from './queue';

export interface QueueTaskAction<F extends UnknownFunction = UnknownFunction> {
  fn: F;
  args: Parameters<F>;
}

type QueueTaskActionWithPromise<F extends UnknownFunction = UnknownFunction> = QueueTaskAction<F> & {
  resolve: PromiseResolve<Awaited<ReturnType<F>>>;
  reject: PromiseReject;
};

class QueueTask {
  #queue = new Queue<QueueTaskActionWithPromise>();
  public get queue(): ReadonlyQueue<QueueTaskActionWithPromise> {
    return this.#queue;
  }

  #running = false;
  public get running(): boolean {
    return this.#running;
  }

  public dispatch = async <F extends UnknownFunction>(
    fn: F,
    ...args: Parameters<F>
  ): Promise<Awaited<ReturnType<F>>> => {
    const { promise, resolve, reject } = new ManualPromise<Awaited<ReturnType<F>>>();
    const action: QueueTaskActionWithPromise<F> = { fn, args, resolve, reject };
    this.#queue.enqueue(action as QueueTaskActionWithPromise);
    await sleep(0);
    this.#loop();

    return await promise;
  };

  #loop = once(async () => {
    return this.#queue.dequeueEach(this.#runner, true);
  });

  #runner = async (action: QueueTaskActionWithPromise) => {
    const { fn, args, resolve, reject } = action;
    this.#running = true;
    try {
      const result = fn(...args);

      if (result instanceof Promise) {
        return await result.then(resolve).catch(reject);
      }

      return resolve(result);
    } catch (error) {
      reject(error);
    } finally {
      this.#running = false;
    }
  };
}

export default QueueTask;
